/* Pennie theme — small client-side helpers
 * - Carousel arrows for featured collections (scroll-snap-driven)
 * - AJAX add-to-basket from product cards
 * - Drawer (mobile menu) + expandable search pill
 */

/* Remove the .is-preloading class set by the inline script in
   theme.liquid <head>. Two rAFs ensure we wait until AFTER the first
   paint with the final cascade applied — that way no transitions
   fire during the initial style settle, but transitions resume
   working for subsequent state changes. */
requestAnimationFrame(function () {
  requestAnimationFrame(function () {
    document.documentElement.classList.remove('is-preloading');
  });
});

// ---- Auto-apply the FREESHIPCA promo code ------------------------------
// Pre-applies the FREESHIPCA discount code to every visitor's session so
// the customer does not have to type it. The customer can still remove it
// at checkout.
//
// NOTE: this is theme-driven auto-apply, NOT a Shopify-native automatic
// discount. FREESHIPCA is a *code* discount in admin, and Shopify has no
// admin setting to auto-apply a *code* at checkout. The only native ways
// to apply it without the customer typing it are:
//   (a) Automatic discount (Promote it as an automatic discount in admin) —
//       applies to everyone who qualifies, but then there is no visible code.
//   (b) Shareable discount link (admin: Promote > Get a shareable discount
//       code) — a /discount/FREESHIPCA URL that auto-applies, but ONLY for
//       visitors who arrive through that link/QR, not direct/organic traffic.
// Both were considered (Session 24). We keep this theme-JS approach because
// it applies the code to EVERY session regardless of how the visitor arrived,
// while still leaving a visible, removable code at checkout. If that
// trade-off ever changes, delete this whole block and use (a) or (b).
//
// Mechanism: Shopify's Cart AJAX API, POST /cart/update.js with
// `{ discount: '<code>' }`, attaches the discount to the cart server-side.
// Same-origin (Safari-ITP safe). The discount travels with the cart token
// through to checkout where the code shows pre-applied with a removable "x".
// The code is destination-restricted to CA in admin, so Shopify only honors
// it for Canadian destinations; for other destinations it is a no-op.
//
// Replaces an older `/discount/<code>` approach which relied on a cookie
// that Shopify stopped setting reliably on many stores around May 2025.
//
// ⚠ Dev-loop caveat: Shopify CLI 3.89.0+ returns HTTP 401 on cart endpoints
// at localhost:9292 (acknowledged regression, Feb 3, 2026). To test locally,
// downgrade to CLI 3.88.0 or test on the live storefront. Production is
// unaffected — this code works fine in production.
//
// Conditional: applies FREESHIPCA only when the cart has NO code (never wipes a
// code the shopper already has), Canada-only, and not right after an in-session
// manual removal (so a later revisit with an empty cart re-applies it).
(function () {
  try {
    // FREESHIPCA is Canada-only — only auto-apply when the visitor's market is
    // Canada. PENNIE_GEO_COUNTRY is rendered by theme.liquid from
    // localization.country.iso_code (Shopify Markets geo). No global = don't apply.
    if ((window.PENNIE_GEO_COUNTRY || '').toUpperCase() !== 'CA') return;
    // If the shopper removed the cart's code this session, don't fight them by
    // re-adding it. Cleared when the browser session ends, so a later revisit re-applies.
    if (sessionStorage.getItem('pennie-freeship-removed') === '1') return;
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        // A code is already applied → leave it (don't wipe FREESHIPCA OR a shopper-
        // entered code). Only auto-apply into an empty discount slot.
        var hasCode = (cart.discount_codes || []).some(function (d) { return d.applicable !== false; });
        if (hasCode) return;
        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ discount: 'FREESHIPCA' }),
          credentials: 'same-origin'
        });
      })
      .catch(function () { /* silent — never block UX over a discount */ });
  } catch (e) {}
})();

// ---- Cart destination sync (delivery.addresses ↔ URL market) -----------
// Why this exists: Shopify's checkout reads cart.delivery.addresses[selected]
// to determine the cart's shipping destination. That layer is independent of
// (a) the URL prefix, (b) cart_currency cookie, (c) /localization buyer
// market preference. Once written, it persists on the cart token until
// explicitly overwritten — Shopify's checkout UI "remove address" button only
// clears the editable form fields, not the cart-level entry. Discounts with
// destination restrictions (e.g. FREESHIPCA: Canada only) are evaluated
// against this layer, so a stale entry silently invalidates them.
//
// What this does: on every page load, write a country-only delivery address
// matching the URL's market. CA URL → country: Canada; /en-us/ → United States.
// Country only, no street/city/zip — so the customer's checkout form stays
// blank for them to fill in normally. Their explicit checkout submission
// writes to the same layer AFTER this runs, so their choice wins over ours.
//
// Fire-and-forget. Storefront API mutation, idempotent, no UI dependency.
// Token is read from /cart.js (Shopify returns the full "<token>?key=..."
// form needed by the Storefront API; the `cart` cookie alone lacks the key).
(function () {
  try {
    var path = window.location.pathname;
    var onUSMarket = path === '/en-us' || path.indexOf('/en-us/') === 0;
    var country = onUSMarket ? 'United States' : 'Canada';
    var STOREFRONT_TOKEN = '612b40156dd791da9904e8b3811d0828';

    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (!cart || !cart.token) return;
        var cartId = 'gid://shopify/Cart/' + cart.token;
        var query =
          'mutation($cartId: ID!, $bi: CartBuyerIdentityInput!) {' +
          '  cartBuyerIdentityUpdate(cartId: $cartId, buyerIdentity: $bi) {' +
          '    cart { id } userErrors { field message }' +
          '  }' +
          '}';
        return fetch('/api/2025-07/graphql.json', {
          method: 'POST',
          headers: {
            'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            query: query,
            variables: {
              cartId: cartId,
              bi: { deliveryAddressPreferences: [{ deliveryAddress: { country: country } }] }
            }
          })
        });
      })
      .catch(function () { /* never block — destination sync is best-effort */ });
  } catch (e) {}
})();

(function () {
  'use strict';

  // ---- Ghost-click guard --------------------------------------------------
  // iOS Safari: tapping a link navigates, then ~300ms later dispatches a
  // SYNTHESIZED "ghost" click at the same screen coordinates — on the NEW
  // page. If a product-card tap was in the top-right of the grid, that ghost
  // click lands on the freshly-loaded header's search / cart icons (they sit
  // adjacent in .header__actions) and flashes the drawer open over the
  // loading page. A genuine tap always fires pointerdown→click on the same
  // element; a ghost click arrives as a lone click with no preceding
  // pointerdown on this page. So we record the last real pointerdown and only
  // honor a toggle click that was preceded by a pointerdown on the same
  // toggle, moments earlier.
  var lastPointerDownEl = null;
  var lastPointerDownAt = 0;
  document.addEventListener('pointerdown', function (event) {
    lastPointerDownEl = event.target;
    lastPointerDownAt = Date.now();
  }, true);
  // True only when the click was preceded by a real pointerdown inside the
  // same element (selector) within the last 700ms — i.e. a genuine tap/click,
  // not a post-navigation ghost click.
  function isGenuineActivation(selector) {
    if (!lastPointerDownEl || !lastPointerDownEl.closest) return false;
    if (Date.now() - lastPointerDownAt > 700) return false;
    return !!lastPointerDownEl.closest(selector);
  }

  // ---- Image-theft client-side blockers (Step 4) --------------------------
  // Casual-deterrent layer on every <img> site-wide. Closes the four
  // common "I'll just save that" paths without any visual change to the
  // page:
  //   - contextmenu  → right-click → "Save Image As" / "Open image in new
  //                    tab" never appears (same vector Google killed in 2018
  //                    when they removed the "View image" button)
  //   - dragstart    → image cannot be dragged to the desktop or into
  //                    another tab
  //   - keydown S    → Cmd+S / Ctrl+S "Save Page As" keyboard shortcut is
  //                    suppressed (the File-menu path remains unblockable
  //                    by JS — that's an OS-level menu)
  // The CSS counterpart in base.css adds user-drag:none + user-select:none
  // + -webkit-touch-callout:none so iOS long-press also doesn't show the
  // Save Image / Add to Photos menu. Together these stop ~80% of casual
  // savers. The undefeatable floor (screenshot, mitmproxy, DevTools network
  // capture) is handled by the source cap (Step 5) + Cloudflare Worker
  // (Step 7) layers — see session-notes for the full defense-in-depth
  // stack.
  document.addEventListener('contextmenu', function (e) {
    if (e.target && e.target.tagName === 'IMG') {
      e.preventDefault();
    }
  }, { passive: false });
  document.addEventListener('dragstart', function (e) {
    if (e.target && e.target.tagName === 'IMG') {
      e.preventDefault();
    }
  }, { passive: false });
  document.addEventListener('keydown', function (e) {
    // Scope: only the literal Save shortcut. Don't touch Escape (drawer
    // close), arrow keys (carousel nav), or any modifier combo other than
    // bare Cmd/Ctrl + S.
    if ((e.metaKey || e.ctrlKey) && !e.shiftKey && !e.altKey &&
        (e.key === 's' || e.key === 'S')) {
      e.preventDefault();
    }
  }, { passive: false });

  // ---- Carousel ------------------------------------------------------------
  // Branched: mobile (≤749) uses Embla for infinite-loop center-snap; tablet
  // and desktop (≥750) use the ORIGINAL committed (c4b13ef) native scroll-snap
  // arrow paging — exactly the pre-Embla behaviour, restored. CSS in
  // featured-collection.css mirrors the same 750px breakpoint.
  //
  // Each carousel sets itself up on load AND re-sets-up on every 749px
  // breakpoint cross (matchMedia change). Without that, whichever mode was
  // active at load time stays bound while the CSS swaps to the other layout —
  // resizing dev viewport between mobile↔tablet would leave the wrong
  // engine running (Embla cards in scroll-snap layout, or vice versa).
  // Teardown uses an AbortController for the arrow listeners and `.destroy()`
  // on the Embla instance, so each setup() call starts from a clean state.
  function initCarousel(root) {
    const track = root.querySelector('[data-carousel-track]');
    const prev = root.querySelector('[data-carousel-prev]');
    const next = root.querySelector('[data-carousel-next]');
    if (!track) return;

    const mql = window.matchMedia('(max-width: 749px)');
    let embla = null;
    let abortCtrl = null;

    function teardown() {
      if (embla) { embla.destroy(); embla = null; }
      if (abortCtrl) { abortCtrl.abort(); abortCtrl = null; }
    }

    function setup() {
      teardown();
      abortCtrl = new AbortController();
      const signal = abortCtrl.signal;

      // Mobile branch — Embla infinite loop center-snap.
      if (mql.matches && typeof window.EmblaCarousel === 'function') {
        const viewport = root.querySelector('[data-carousel-viewport]');
        if (viewport) {
          embla = window.EmblaCarousel(viewport, { loop: true, align: 'center' });
          if (prev) prev.addEventListener('click', () => embla && embla.scrollPrev(), { signal });
          if (next) next.addEventListener('click', () => embla && embla.scrollNext(), { signal });
          window.addEventListener('load', () => embla && embla.reInit(), { signal });
          return;
        }
      }

      // Tablet/desktop branch — original c4b13ef native scroll-snap paging.
      function pageWidth() {
        // Scroll exactly one viewport worth of the track (which already shows N cards)
        return Math.max(track.clientWidth - 32, 320);
      }
      // Both arrows wrap: prev jumps to the end at the left edge, next jumps
      // back to the start at the right edge, so paging is continuous in both
      // directions and neither arrow ever hides.
      if (prev) prev.addEventListener('click', () => {
        if (track.scrollLeft <= 1) {
          track.scrollTo({ left: track.scrollWidth - track.clientWidth, behavior: 'smooth' });
        } else {
          track.scrollBy({ left: -pageWidth(), behavior: 'smooth' });
        }
      }, { signal });
      if (next) next.addEventListener('click', () => {
        const max = track.scrollWidth - track.clientWidth - 1;
        if (track.scrollLeft >= max) {
          track.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          track.scrollBy({ left: pageWidth(), behavior: 'smooth' });
        }
      }, { signal });
    }

    setup();
    mql.addEventListener('change', setup);
  }

  document.querySelectorAll('[data-carousel]').forEach(initCarousel);

  // ---- Catalogue nav button → opens the drawer ---------------------------
  // The desktop "Catalogue" nav button (data-catalogue-toggle) used to open
  // a separate dropdown panel. That panel was deleted on 2026-06-29; the
  // button now toggles the SAME slide-out .header__drawer that the mobile
  // hamburger opens, so the drawer is the unified menu at every viewport.
  // The drawer's own close handlers (X, Esc, outside-click, backdrop)
  // continue to handle closing — no separate close logic needed here.
  document.addEventListener('click', function (event) {
    var toggle = event.target.closest && event.target.closest('[data-catalogue-toggle]');
    if (!toggle) return;
    event.preventDefault();
    var drawer = document.querySelector('details[data-pennie-drawer]') ||
                 document.querySelector('.header__drawer');
    if (!drawer) return;
    if (drawer.hasAttribute('open')) {
      closeDrawer(drawer);
    } else {
      drawer.setAttribute('open', '');
      toggle.setAttribute('aria-expanded', 'true');
    }
  });

  // ---- Drawer (mobile/hamburger menu) -------------------------------------
  // Drawer is a native <details data-pennie-drawer> element. <summary> opens
  // it; we wire close-button, ESC, link click, and outside-click to close.
  // Closing is animated: JS adds .is-closing (which runs the slide-out
  // keyframe), waits for it to finish, then actually removes [open].

  const DRAWER_CLOSE_MS = 320;   // matches the tablet/mobile menu drawer slide (header.css @media ≤989) so the [open] removal lands at the end of the animation. Desktop still runs the 240ms CSS close animation, but it has long finished by 320ms — the extra 80ms wait is invisible because the drawer is already at its end-state (translateX(-100%)).

  function closeDrawer(drawer) {
    if (!drawer || !drawer.hasAttribute('open') || drawer.classList.contains('is-closing')) return;
    drawer.classList.add('is-closing');
    setTimeout(function () {
      drawer.removeAttribute('open');
      drawer.classList.remove('is-closing');
    }, DRAWER_CLOSE_MS);
  }

  document.addEventListener('click', function (event) {
    // Close button inside any drawer
    const closeBtn = event.target.closest('[data-drawer-close]');
    if (closeBtn) {
      closeDrawer(closeBtn.closest('[data-pennie-drawer]'));
      event.preventDefault();
      return;
    }
    // Click on a drawer link → close after navigation begins
    const drawerLink = event.target.closest('[data-pennie-drawer] a');
    if (drawerLink) {
      closeDrawer(drawerLink.closest('[data-pennie-drawer]'));
      return;
    }
    // Outside-click: close any open drawers if click is outside the panel.
    // Exclude clicks on the toggle buttons themselves (hamburger summary +
    // desktop "Catalogue" nav button) so an open-click doesn't immediately
    // close via this handler. Each toggle's own click logic owns the open/
    // close decision.
    if (event.target.closest('[data-catalogue-toggle]') ||
        event.target.closest('[data-drawer-summary], summary')) {
      return;
    }
    document.querySelectorAll('[data-pennie-drawer][open]').forEach(function (d) {
      if (!d.contains(event.target)) closeDrawer(d);
    });
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') {
      document.querySelectorAll('[data-pennie-drawer][open]').forEach(closeDrawer);
      closeSearch();
    }
  });

  // ---- Drawer Shop-nav — Level 1 ⇄ Level 2 + category selector ------------
  // The mobile/tablet drawer has a two-view text menu inside the topblock:
  //   Level 1 — All Products / What's New / Shop By Style (button)
  //   Level 2 — Back button + two-column "Shop By Style" view
  //
  // Level 2 layout (House-of-CB pattern):
  //   LEFT column  — category labels (Colour / Metal / Size). Tapping does
  //                  NOT navigate; it just activates the category, which
  //                  swaps which list is visible in the right column.
  //   RIGHT column — value list for the active category. Tapping a value
  //                  navigates to /collections/all with that filter.
  //
  // Tapping "Shop By Style" in Level 1 hides Level 1 and shows Level 2.
  // Tapping the back button in Level 2 reverses it AND resets the category
  // selector back to the first (Colour) so re-entering starts clean. The
  // drawer's close handler does the same reset, so re-opening the whole
  // drawer always starts at the top with Colour active.
  // Reset Level 1 visible, all Level 2 sub-views hidden. Called on back
  // button and on drawer close so re-entering always starts at the top.
  // Show exactly one shop-nav view per [data-shopnav]. Every view carries
  // data-level + data-cat ("root" = Level 1, "shopby" = Level 2, and
  // charm/colour/metal/size = Level 3). [data-into="<key>"] opens the view
  // with that data-cat; [data-back="<key>"] returns to it.
  function showShopnavView(nav, key) {
    nav.querySelectorAll('[data-level]').forEach(function (view) {
      view.hidden = view.getAttribute('data-cat') !== key;
    });
  }
  // Reset to Level 1 (root). Called on drawer close so re-entering always
  // starts at the top.
  function resetShopnav() {
    document.querySelectorAll('[data-shopnav]').forEach(function (nav) {
      showShopnavView(nav, 'root');
    });
  }
  document.addEventListener('click', function (e) {
    var into = e.target.closest && e.target.closest('[data-into]');
    if (into) {
      e.preventDefault();
      var nav = into.closest('[data-shopnav]');
      if (!nav) return;
      showShopnavView(nav, into.getAttribute('data-into'));
      return;
    }
    var back = e.target.closest && e.target.closest('[data-back]');
    if (back) {
      e.preventDefault();
      var navB = back.closest('[data-shopnav]');
      if (!navB) return;
      showShopnavView(navB, back.getAttribute('data-back') || 'root');
    }
  });
  // Reset to Level 1 + first category whenever the drawer closes.
  document.addEventListener('click', function (e) {
    if (!e.target.closest) return;
    if (e.target.closest('[data-drawer-close]') || e.target.closest('.header__drawer-backdrop')) {
      // Wait for the drawer's close animation to finish before resetting,
      // so the reset doesn't visibly flash mid-slide.
      setTimeout(resetShopnav, 360);
    }
  });

  // ---- Header search ----------------------------------------------
  // Trigger button (in actions cluster) toggles a sibling panel that
  // contains the input + clear-X.
  //   - Click trigger → open + focus input
  //   - Click trigger again with text → submit
  //   - Click trigger again with empty input → close
  //   - Click clear-X → empty the input, keep open, refocus
  //   - Click outside with empty input → close
  //   - ESC → close
  const searchPanel = document.querySelector('[data-search-form]');
  const searchToggle = document.querySelector('[data-search-toggle]');
  const searchInput = searchPanel && searchPanel.querySelector('input[type="search"]');
  const searchClear = searchPanel && searchPanel.querySelector('[data-search-clear]');

  function openSearch() {
    if (!searchPanel) return;
    searchPanel.classList.add('is-open');
    searchPanel.setAttribute('aria-hidden', 'false');
    // Flips the header to solid paper + ink on tablet/mobile (CSS, ≤989px) so
    // the nav stays legible behind the dropped full-width search bar.
    document.body.classList.add('has-search-open');
    searchToggle && searchToggle.setAttribute('aria-expanded', 'true');
    if (searchInput) {
      searchInput.removeAttribute('tabindex');
      setTimeout(() => searchInput.focus(), 60);
    }
  }
  function closeSearch() {
    if (!searchPanel) return;
    searchPanel.classList.remove('is-open');
    searchPanel.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('has-search-open');
    searchToggle && searchToggle.setAttribute('aria-expanded', 'false');
    searchInput && searchInput.setAttribute('tabindex', '-1');
    // Mobile/tablet modal: clear the query on close so reopening starts fresh
    // (the results dropdown clears via search-suggest.js when .is-open drops).
    if (searchInput && window.matchMedia('(max-width: 989px)').matches) {
      searchInput.value = '';
    }
  }
  function syncClear() {
    if (!searchClear || !searchInput) return;
    searchClear.classList.toggle('is-visible', searchInput.value.length > 0);
    if (searchInput.value.length > 0) searchClear.removeAttribute('hidden');
    else searchClear.setAttribute('hidden', '');
  }

  if (searchToggle) {
    searchToggle.addEventListener('click', function (event) {
      event.preventDefault();
      // Ignore post-navigation ghost clicks (see ghost-click guard up top).
      if (!isGenuineActivation('[data-search-toggle]')) return;
      if (searchPanel.classList.contains('is-open')) {
        if (searchInput.value.trim()) {
          searchPanel.submit();
        } else {
          closeSearch();
        }
      } else {
        openSearch();
      }
    });
  }

  if (searchInput) {
    searchInput.addEventListener('input', syncClear);
  }
  if (searchClear) {
    searchClear.addEventListener('click', function (event) {
      event.preventDefault();
      event.stopPropagation();
      searchInput.value = '';
      syncClear();
      searchInput.focus();
    });
  }

  // Outside-click closes the search ONLY if the input is empty
  document.addEventListener('mousedown', function (event) {
    if (!searchPanel || !searchPanel.classList.contains('is-open')) return;
    if (searchPanel.contains(event.target) || (searchToggle && searchToggle.contains(event.target))) return;
    if (!searchInput.value.trim()) closeSearch();
  });

  // Tablet/mobile modal: the dim backdrop or the close (X) button always closes
  // the search (regardless of input content).
  document.addEventListener('click', function (event) {
    if (event.target.closest('[data-search-backdrop]') || event.target.closest('[data-search-close]')) {
      closeSearch();
    }
  });

  // ---- Sticky header on scroll --------------------------------------------
  // Header is `position: absolute` over the hero. Once the user scrolls past
  // its original bottom edge, switch to `.header--stuck` (fixed, paper bg,
  // slide-down animation). Toggling instead of always-fixed avoids layout
  // shift and keeps the transparent-over-hero look.

  (function stickyHeader() {
    const header = document.querySelector('[data-pennie-header]');
    if (!header) return;
    // The header is `position: fixed` always (CSS). On scroll we:
    //   1. Slide its top from 42px → 0 as the announcement bar scrolls
    //      out of view (no empty gap).
    //   2. Toggle .header--stuck when the hero heading reaches the
    //      header's bottom edge — switches bg/text from transparent +
    //      white to paper + ink.
    /* Measure the SECTION wrapper, not the inner .announcement-bar pill —
       the section now has 4px top padding around the pill for the floating
       look, and the header sticks at top: var(--announcement-height), so
       this needs to include that padding or the header rides up by 4px
       once both elements get stuck and overlap. Falls back to the pill
       itself if the section selector ever changes. */
    const announcementBar =
      document.querySelector('.announcement-bar-section') ||
      document.querySelector('.announcement-bar');
    // Publish announcement-bar height as a CSS variable so the header's
    // default absolute `top` matches the bar's actual rendered height
    // (which grows on iOS Safari to include safe-area-inset-top). Without
    // this, the absolute → fixed handover snaps because CSS top (42px) and
    // the JS pin point (announcementHeight, ~90px on iPhone) disagree.
    function publishAnnouncementHeight() {
      const h = announcementBar ? announcementBar.offsetHeight : 42;
      document.documentElement.style.setProperty('--announcement-height', `${h}px`);
      return h;
    }
    let announcementHeight = publishAnnouncementHeight();
    window.addEventListener('resize', () => { announcementHeight = publishAnnouncementHeight(); });
    // Landing-page-only scroll behaviour. Two anchors:
    //   - heroEl (.pennie-hero): only stick .header--stuck AFTER the hero
    //     is fully scrolled past — i.e. hero's bottom edge has passed above
    //     the header's bottom edge. Before that the header stays transparent
    //     over the hero.
    //   - richBrownsEl (image-with-text section, key: feature_full_width):
    //     while THIS section is currently crossing under the header (any
    //     part of it overlaps the header's y-range), un-stick again so the
    //     header goes back to transparent + light text over the dark
    //     wood/brown photography, then re-stick after the section scrolls
    //     past.
    // Non-landing pages already carry .header--solid on the server-rendered
    // markup, so this JS is a no-op for them (heroEl is null → early return).
    const heroEl = document.querySelector('.pennie-hero');
    // Shopify wraps JSON-template sections as
    // `<div id="shopify-section-template--<TEMPLATE_ID>__<section-key>">`.
    // The `template--<TEMPLATE_ID>__` prefix is theme-install-specific, so a
    // straight `getElementById('shopify-section-feature_full_width')` never
    // matches — suffix-match the section key instead, then fall back to the
    // `.image-with-text` class if the wrapper ever changes shape.
    // We then dive INTO the section to grab the actual dark image container
    // (`.iwt-card__media`) — tracking the section wrapper itself would
    // un-stick the header the moment the section's 30px top paper padding
    // scrolled under the header, showing paper through the (now transparent)
    // header before the dark photo actually arrived. Tracking the image
    // container aligns the un-stick trigger to the visible dark photo edge.
    const richBrownsSection =
      document.querySelector('[id^="shopify-section-"][id$="__feature_full_width"]') ||
      document.querySelector('.image-with-text');
    const richBrownsEl =
      (richBrownsSection && richBrownsSection.querySelector('.iwt-card__media')) ||
      (richBrownsSection && richBrownsSection.querySelector('.iwt-card')) ||
      richBrownsSection;
    let stuck = false;
    let lastTop = -1;

    function onScroll() {
      if (!heroEl) return;                     // non-landing: leave classes alone
      const headerBottom = header.getBoundingClientRect().bottom;
      const heroBottom = heroEl.getBoundingClientRect().bottom;
      // Rule 1: stick only once the hero has fully scrolled past the header.
      let shouldStick = heroBottom <= headerBottom;
      // Rule 2: while the rich-browns section is currently overlapping the
      // header's y-range (its top has crossed above headerBottom AND its
      // bottom hasn't yet crossed above headerBottom), un-stick again so
      // the section reads as its own dark hero-like moment.
      if (shouldStick && richBrownsEl) {
        const rb = richBrownsEl.getBoundingClientRect();
        // 30px offset — the section has 30px of paper padding above the
        // dark image (see --iwt-pad-top in image-with-text.css). Without
        // the offset, the un-stick fires the moment the paper padding
        // slides under the header, which reads as "header went transparent
        // over paper" instead of "over the dark image". Requiring the
        // card top to scroll 30px past the header bottom before un-sticking
        // pushes the trigger past that paper zone so the header only goes
        // transparent once the dark image is genuinely behind it.
        const RB_TRIGGER_OFFSET = 8;
        if (rb.top < headerBottom - RB_TRIGGER_OFFSET && rb.bottom > headerBottom) {
          shouldStick = false;
        }
      }
      if (shouldStick !== stuck) {
        stuck = shouldStick;
        header.classList.toggle('header--stuck', stuck);
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  })();

  // ---- Product card AJAX add-to-basket ------------------------------------

  document.addEventListener('click', function (event) {
    const button = event.target.closest('[data-quick-add]');
    if (!button) return;
    event.preventDefault();
    if (button.disabled) return;

    const variantId = button.getAttribute('data-variant-id');
    if (!variantId) return;

    // Icon-only ATC buttons (collection browse cards) keep their SVG and
    // signal state via [data-state] instead of textContent, which would
    // otherwise destroy the child SVG.
    const iconOnly = button.hasAttribute('data-quick-add-icon');
    const original = iconOnly ? null : button.textContent;
    button.disabled = true;
    if (iconOnly) { button.setAttribute('data-state', 'adding'); }
    else { button.textContent = 'Adding…'; }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
    })
      .then(r => r.json())
      .then(() => {
        if (iconOnly) { button.setAttribute('data-state', 'added'); }
        else { button.textContent = 'Added'; }
        // Refresh the drawer contents + header badge from the server, then
        // slide the cart open so the user sees what they just added.
        // __pennieRefreshCart also syncs every [data-cart-count] badge
        // (with the bump animation) so the header count stays in step.
        return window.__pennieRefreshCart().then(function () {
          window.__pennieOpenCart();
        });
      })
      .catch(() => {
        if (iconOnly) { button.setAttribute('data-state', 'error'); }
        else { button.textContent = 'Try again'; }
      })
      .then(() => {
        setTimeout(() => {
          button.disabled = false;
          if (iconOnly) { button.removeAttribute('data-state'); }
          else { button.textContent = original; }
        }, 1400);
      });
  });

  // ---- Cart drawer --------------------------------------------------------
  // Slide-out basket. The drawer markup lives in sections/cart-drawer.liquid
  // (global via header-group). We never reload the page for cart changes:
  //   - open/close toggles .is-open on [data-cart-drawer] + body scroll-lock
  //   - qty steppers / remove POST to /cart/change.js
  //   - after any mutation we re-fetch the section HTML (Section Rendering
  //     API: /?sections=cart-drawer), swap ONLY [data-cart-drawer-contents],
  //     and sync the header [data-cart-count] badge.

  function openCart() {
    var drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer) return;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('has-cart-open');
  }

  function closeCart() {
    var drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('has-cart-open');
    hideDiscountMsg();
  }

  function syncCartBadge(count) {
    document.querySelectorAll('[data-cart-count]').forEach(function (n) {
      n.textContent = count;
      if (count > 0) {
        n.removeAttribute('hidden');
        n.classList.remove('is-bumped');
        void n.offsetWidth;          // restart the bump keyframe
        n.classList.add('is-bumped');
      } else {
        n.setAttribute('hidden', '');
      }
    });
  }

  // Re-render the drawer contents from the server so prices, qty, subtotal,
  // and empty-state all stay authoritative. Returns a promise.
  function refreshCart() {
    invalidateShipRateCache();   // cart contents may have changed → rate may differ
    return fetch('/?sections=cart-drawer', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var html = data['cart-drawer'];
        if (html == null) return;
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var fresh = parsed.querySelector('[data-cart-drawer-contents]');
        var live = document.querySelector('[data-cart-drawer-contents]');
        // Only swap when the markup actually changed — re-writing identical
        // innerHTML re-creates the <img> nodes and makes them flash on open.
        if (fresh && live && fresh.innerHTML !== live.innerHTML) {
          // Carry the already-computed Shipping + Total across the swap so the
          // server-rendered "Calculated at checkout" placeholder doesn't flash for a
          // frame before renderCartFooter() refills them with the live values.
          var prevShip = live.querySelector('[data-cart-shipping-value]');
          var prevTotal = live.querySelector('[data-cart-total-value]');
          var prevSelect = live.querySelector('[data-cart-ship-country]');
          var prevChip = live.querySelector('[data-cart-codes]');
          var shipHTML = prevShip ? prevShip.innerHTML : null;
          var totalText = prevTotal ? prevTotal.textContent : null;
          var selCountry = prevSelect ? prevSelect.value : null;
          var chipHTML = prevChip ? prevChip.innerHTML : null;
          live.innerHTML = fresh.innerHTML;
          if (shipHTML != null) {
            var nextShip = live.querySelector('[data-cart-shipping-value]');
            if (nextShip) nextShip.innerHTML = shipHTML;
          }
          if (totalText != null) {
            var nextTotal = live.querySelector('[data-cart-total-value]');
            if (nextTotal) nextTotal.textContent = totalText;
          }
          // Carry over the applied-code chip — it's JS-rendered into [data-cart-codes],
          // so the swap leaves it empty until renderCartFooter re-runs. Without this it
          // disappears for a frame on every cart refresh (e.g. qty change, country toggle).
          if (chipHTML != null) {
            var nextChip = live.querySelector('[data-cart-codes]');
            if (nextChip) nextChip.innerHTML = chipHTML;
          }
          // Re-apply cached market-availability sold-out state synchronously, before
          // the browser paints a frame of the (server-rendered as "available") line.
          // Without this, qty changes flash the price + ATC back on a market-excluded
          // line for ~50-200ms until checkMarketAvailability re-applies it async.
          applyCachedMarketSoldOut(live);
          if (selCountry != null) {
            var nextSelect = live.querySelector('[data-cart-ship-country]');
            if (nextSelect) nextSelect.value = selCountry;   // keep the chosen country (no flash to default)
          }
          // Carry the country-toggle visual + input across the swap synchronously,
          // before the browser paints a frame. Otherwise the new server-rendered
          // markup (which may still reflect the previous market on local dev where
          // /localization hasn't committed) flashes for ~1 frame on a CA→US switch.
          if (lastCountryChoice) {
            var nextTog = live.querySelector('[data-country-toggle]');
            if (nextTog) nextTog.classList.toggle('is-us', lastCountryChoice === 'US');
            var nextIn = live.querySelector('[data-country-input]');
            if (nextIn) nextIn.value = lastCountryChoice;
          }
        }
      })
      .then(function () {
        return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
          .then(function (r) { return r.json(); })
          .then(function (cart) { syncCartBadge(cart.item_count); });
      })
      .then(function () {
        renderCartFooter();
        syncCheckoutGate();
        checkMarketAvailability();   // probe each cart line's PDP in the current market
        // After every cart re-render, re-apply the user's last country pick. The
        // server's just-baked markup may carry the previous country (Markets context
        // lag, or local dev /localization not committing), which would snap the
        // toggle back without this.
        if (lastCountryChoice) syncCountryControls(lastCountryChoice);
      });
  }
  // Expose so the add-to-cart handler (and future callers) can refresh + open.
  window.__pennieRefreshCart = refreshCart;
  window.__pennieOpenCart = openCart;

  // ---- Cart drawer footer: discount codes + live shipping + total ---------
  // Subtotal and any value-discount rows are server-rendered by
  // sections/cart-drawer.liquid (real money straight from Shopify). This module
  // fills the dynamic parts: the applied-code chip, the Shipping row (live rate
  // from /cart/shipping_rates.json for the selected country), struck-through free
  // shipping when a valid shipping code is applied, and the Total.
  //
  // Regional codes use a NAMING-SUFFIX convention read by the theme (NOT a
  // hardcoded per-code table): a code ending in CA / -CA is Canada-only, US /
  // -US is US-only, anything else applies to any country. The suffix is a
  // preview gate only — Shopify still enforces each code's real rules at
  // checkout. Full spec + caveats: docs/DISCOUNT-CODE-SUFFIX-CONVENTION.md.
  // Holds the shopper's last country pick for this page session, so any cart
  // re-render (which re-bakes the server's idea of country into the new HTML)
  // can be re-applied without snapping the toggle back. Null until first switch.
  var lastCountryChoice = null;
  // Country -> shipping-rate-API anchor. Rates are country-flat, so any in-country
  // province/zip works (Shopify only uses them to pick the zone). Moved here from the
  // old <option> data attrs when the dropdown became a toggle.
  var SHIP_COUNTRIES = {
    CA: { name: 'Canada',        province: 'Ontario',  zip: 'M5V 2T6' },
    US: { name: 'United States', province: 'New York', zip: '10001'   }
  };
  var SHIP_ANCHORS = { CA: SHIP_COUNTRIES.CA.name, US: SHIP_COUNTRIES.US.name };
  var shipRateCache = {};

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function formatMoney(cents) {
    return '$' + (Math.round(cents) / 100).toFixed(2);
  }
  // Country a code is restricted to by its trailing suffix, or null (= any).
  function codeCountry(code) {
    var c = (code || '').toUpperCase().trim();
    if (c.slice(-2) === 'CA') return 'CA';
    if (c.slice(-2) === 'US') return 'US';
    return null;
  }
  function countryName(cc) { return SHIP_ANCHORS[cc] || cc; }

  function getSelectedCountry() {
    // Read from the localization form's hidden input (header form is the source of
    // truth; cart form mirrors it). Falls back to the server-rendered geo on the
    // footer for first paint before any switch.
    var input = document.querySelector('[data-header-country-form] [data-country-input]') ||
                document.querySelector('[data-cart-country-form] [data-country-input]');
    if (input && input.value) return input.value;
    var footer = document.querySelector('[data-cart-footer]');
    return (footer && footer.getAttribute('data-geo-country')) || 'CA';
  }

  function discountMsgEl() { return document.querySelector('[data-cart-discount-msg]'); }
  function showDiscountMsg(text) {
    var el = discountMsgEl(); if (!el) return;
    el.textContent = text; el.hidden = false;
  }
  function hideDiscountMsg() {
    var el = discountMsgEl(); if (!el) return;
    el.hidden = true; el.textContent = '';
  }

  // ============================================================================
  // ⚠️ HARDCODED SHIPPING RATES — MUST BE KEPT IN SYNC WITH SHOPIFY ADMIN ⚠️
  // ----------------------------------------------------------------------------
  // Shopify's /cart/shipping_rates.json endpoint returns inflated values for
  // non-shop-currency markets (e.g. our shop currency is CAD, so US/USD rates
  // come back ~2% high — $4 USD configured → $4.08 returned). Checkout uses a
  // different code path and shows the correct configured rate, so the cart
  // drawer would silently disagree with the checkout total. To avoid showing
  // customers a number that doesn't match what they pay, we bypass the buggy
  // endpoint and inject the configured rates directly.
  //
  // 🔴 IF YOU CHANGE A SHIPPING RATE IN SHOPIFY ADMIN, UPDATE THESE TOO. 🔴
  //   Admin location: Settings → Shipping and delivery → General profile
  //                   AND Markets → [Country] → Shipping (per-market overrides)
  //   Values are in CENTS, in the rate's native currency (CA=CAD, US=USD).
  //
  // Same warning is also in README.md (search "HARDCODED_SHIP_CENTS") and
  // CLAUDE.md so future sessions surface it.
  // ============================================================================
  var HARDCODED_SHIP_CENTS = {
    CA: 1200,  // $12.00 CAD — General profile "Domestic" zone, Standard rate
    US:  400   // $4.00 USD  — General profile "US Cross-border" zone, Standard International
  };

  // Returns the configured shipping rate (cents) for a country. The cache is
  // kept (callers depend on the Promise API) but is effectively a no-op now
  // that lookups are O(1) constant.
  function fetchShipRate(country) {
    var cents = HARDCODED_SHIP_CENTS[country];
    if (cents == null) return Promise.reject();
    shipRateCache[country] = cents;
    return Promise.resolve(cents);
  }
  // Drop the shipping-rate cache. Called from every cart-mutation entry point.
  function invalidateShipRateCache() { shipRateCache = {}; }

  // Is this applied code a shipping (free-shipping) code? Free-shipping codes
  // reduce shipping, not the goods total, so they don't appear in the cart's
  // value-discount applications. Prefer the explicit type field when present.
  function isShippingCode(applied, cart) {
    if (applied.type) return applied.type === 'shipping';
    var apps = (cart.cart_level_discount_applications || []);
    var reducesGoods = apps.some(function (a) {
      return (a.title || '').toUpperCase() === (applied.code || '').toUpperCase();
    });
    return !reducesGoods && (cart.total_discount || 0) === 0;
  }

  function renderChip(applied) {
    var ul = document.querySelector('[data-cart-codes]');
    if (!ul) return;
    if (!applied) { ul.innerHTML = ''; return; }
    var code = escapeHtml(applied.code);
    ul.innerHTML =
      '<li class="cart-drawer__code-chip">' +
        '<span>' + code + '</span>' +
        '<button type="button" data-cart-code-remove aria-label="Remove ' + code + '">&times;</button>' +
      '</li>';
  }

  // Orchestrates the footer: chip + shipping + total. Pass a cart object to paint from
  // it directly (skips a /cart.js round-trip); omit it to fetch fresh.
  function renderCartFooter(cartData) {
    var footer = document.querySelector('[data-cart-footer]');
    if (!footer) return;
    var country = getSelectedCountry();
    var shipValueEl = footer.querySelector('[data-cart-shipping-value]');
    var totalEl = footer.querySelector('[data-cart-total-value]');

    function paint(cart) {
      var goods = cart.total_price || 0;   // after value discounts, before shipping
      // Show the applied code only if Shopify is honouring it (applicable) AND it's
      // valid for the selected country by suffix (…CA under Canada, …US under US; no
      // suffix = any). The applicable check keeps typos / non-applying codes out; the
      // suffix check hides a country-specific code when you switch countries.
      var applied = (cart.discount_codes || []).filter(function (d) {
        if (d.applicable === false) return false;
        var cc = codeCountry(d.code);
        return cc === null || cc === country;
      })[0] || null;
      renderChip(applied);

      // Value-discount row(s) ("-$X.XX" under the Subtotal). Rendered here in
      // JS so applying / removing a code via the fast path (no /?sections=
      // cart-drawer re-render) still updates the row.
      //
      // Two flavours of code-discount produce a row:
      //   • Cart-level codes — populate cart.cart_level_discount_applications
      //     with one entry per application (title + total_allocated_amount).
      //     One row per entry, mirrors the server-rendered Liquid loop.
      //   • Line-level codes — DON'T populate cart_level_discount_applications
      //     (they live in items[].line_level_discount_allocations instead).
      //     They still reduce cart.total_price and bump cart.total_discount,
      //     so we synthesise a single row using the applied code as the title
      //     and the difference (original_total_price - total_price) as the
      //     amount.
      //
      // Shipping codes never affect goods, so they correctly produce no row
      // here — that's handled separately by the "Shipping: ~~$X.XX~~ $0.00"
      // treatment below.
      var summary = footer.querySelector('.cart-drawer__summary');
      if (summary) {
        summary.querySelectorAll('.cart-drawer__sum-row--save').forEach(function (r) { r.remove(); });
        var shipRow = summary.querySelector('[data-cart-shipping-row]');
        function injectRow(title, amountCents) {
          var row = document.createElement('div');
          row.className = 'cart-drawer__sum-row cart-drawer__sum-row--save';
          row.innerHTML = '<dt>' + escapeHtml(title) + '</dt>' +
                          '<dd>&minus;' + formatMoney(amountCents) + '</dd>';
          if (shipRow) summary.insertBefore(row, shipRow);
          else summary.appendChild(row);
        }
        var apps = cart.cart_level_discount_applications || [];
        if (apps.length) {
          // Show "Discount" as the label, not the Shopify-provided app.title
          // (which is usually the raw code like "SAVE10"). Cleaner read in the
          // drawer; the code itself still shows above in the applied-code chip.
          apps.forEach(function (app) {
            injectRow('Discount', app.total_allocated_amount || 0);
          });
        } else if (applied && !isShippingCode(applied, cart)) {
          // Fallback for line-level discounts: compute savings off the cart.
          var savings = (cart.original_total_price || 0) - (cart.total_price || 0);
          if (savings > 0) injectRow('Discount', savings);
        }
      }

      var shippingFree = applied && isShippingCode(applied, cart) &&
        (codeCountry(applied.code) === null || codeCountry(applied.code) === country);

      fetchShipRate(country)
        .then(function (cents) {
          if (shippingFree) {
            if (shipValueEl) shipValueEl.innerHTML = '<s>' + formatMoney(cents) + '</s> $0.00';
            if (totalEl) totalEl.textContent = formatMoney(goods);
          } else {
            if (shipValueEl) shipValueEl.textContent = formatMoney(cents);
            if (totalEl) totalEl.textContent = formatMoney(goods + cents);
          }
        })
        .catch(function () {
          if (shipValueEl) shipValueEl.textContent = 'Calculated at checkout';
          if (totalEl) totalEl.textContent = formatMoney(goods);
        });
    }

    if (cartData) { paint(cartData); return; }
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(paint)
      .catch(function () { /* 401 locally: keep server-rendered values */ });
  }
  window.__pennieRenderCartFooter = renderCartFooter;

  // Sync the drawer after a discount change. FAST path (no section re-render → no
  // country-select / placeholder flash, fewer round-trips) when there's no
  // server-rendered value-discount row to add or remove — which is the case for
  // shipping codes. Full re-render only when a % / $ off row needs to appear/disappear.
  function syncAfterCartChange(cart) {
    invalidateShipRateCache();   // cart just mutated — rate may differ
    var hasServerRow = !!document.querySelector('.cart-drawer__sum-row--save') ||
      (cart && (cart.cart_level_discount_applications || []).length > 0);
    if (hasServerRow) return refreshCart();
    // IMPORTANT: do NOT pass `cart` to renderCartFooter here. The cart returned
    // from /cart/update.js has a quirk: when a free-shipping discount has just
    // been removed, its total_price still includes the (now-gone) shipping value
    // for a moment. /cart.js gives the clean canonical state, so refetch.
    // (Reproduced via Playwright: removing FREESHIPCA from a $56 cart returned
    // total_price=6800 in the update response but 5600 from /cart.js — the
    // update response was inflating goods by exactly the shipping amount.)
    renderCartFooter();
    if (cart) syncCartBadge(cart.item_count);
    return Promise.resolve();
  }

  // Apply a discount code. Because /cart/update.js REPLACES the code set, applying an
  // invalid code would knock off an already-applied valid one — so we remember the
  // current valid code and restore it if the new code turns out invalid. On invalid we
  // do NOT refresh the drawer region (that would wipe the typed text + warning); the
  // warning stays until the shopper types again or closes the cart.
  function postDiscount(value) {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ discount: value })
    }).then(function (r) { return r.json(); });
  }
  document.addEventListener('submit', function (event) {
    var form = event.target.closest('[data-cart-discount-form]');
    if (!form) return;
    event.preventDefault();
    var input = form.querySelector('[data-cart-discount-input]');
    var code = ((input && input.value) || '').trim();
    if (!code) return;
    var country = getSelectedCountry();
    var cc = codeCountry(code);
    if (cc && cc !== country) {
      // Wrong country by suffix: reject WITHOUT touching the cart, so a valid applied
      // code is never disturbed. Keep the typed text + warning.
      showDiscountMsg(code.toUpperCase() + " isn't valid for " + countryName(country) + '.');
      return;
    }
    var btn = form.querySelector('[data-cart-discount-apply]');
    if (btn) btn.disabled = true;
    function reenable() { if (btn) btn.disabled = false; }

    // Remember the currently-applied valid code so we can put it back on failure.
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var prev = (cart.discount_codes || []).filter(function (d) { return d.applicable !== false; })[0];
        var prevCode = prev ? prev.code : '';
        return postDiscount(code).then(function (cart2) {
          var found = (cart2.discount_codes || []).filter(function (d) {
            return (d.code || '').toUpperCase() === code.toUpperCase();
          })[0];
          if (found && found.applicable !== false) {
            // Valid → it replaced whatever was applied. Clear field, drop warning, sync
            // (fast path for shipping codes: no section re-render, no country flash).
            hideDiscountMsg();
            if (input) input.value = '';
            return syncAfterCartChange(cart2).then(reenable);
          }
          // Invalid → restore the previous valid code (or clear if there was none).
          // Keep the typed text + warning; update only the chip (no full re-render).
          showDiscountMsg('Enter a valid discount code.');
          return postDiscount(prevCode).then(function () { renderCartFooter(); reenable(); });
        });
      })
      .catch(function () { showDiscountMsg('Enter a valid discount code.'); reenable(); });
  });

  // Remove the applied code (clears to empty). Flag the removal so the Canada
  // auto-apply doesn't immediately re-add it this session; a later revisit (new
  // session) with an empty cart re-applies it.
  document.addEventListener('click', function (event) {
    var rm = event.target.closest('[data-cart-code-remove]');
    if (!rm) return;
    event.preventDefault();
    rm.disabled = true;
    try { sessionStorage.setItem('pennie-freeship-removed', '1'); } catch (e) {}
    // Optimistic: clear the chip immediately so it feels instant, then sync.
    renderChip(null);
    hideDiscountMsg();
    postDiscount('')
      .then(function (cart) { return syncAfterCartChange(cart); })
      .catch(function () { renderCartFooter(); });   // restore the chip if the call failed
  });

  // Country switching is a REAL Shopify localization change — it POSTs to
  // /localization and updates the buyer's Market context server-side (products,
  // currency, shipping, discount validation). The header toggle and the cart-drawer
  // dropdown both submit the same form; we intercept here so the page doesn't
  // navigate, then refresh the cart in the new context and sync both controls.
  function switchCountry(country) {
    if (country !== 'CA' && country !== 'US') return Promise.resolve();
    // Optimistically sync the UI so the toggle visually flips immediately,
    // even if the navigation takes a beat to start.
    syncCountryControls(country);

    // Pattern 2: Shopify Markets URL-prefix navigation + /localization POST.
    // URL prefix tells Shopify how to RENDER the page (CA prices vs US prices
    // via /en-us/). /localization explicitly REBINDS the cart token + session
    // cookies to the chosen market on Shopify's server. Without the POST, the
    // cart token's market binding stays stuck on whatever the cart was first
    // created in — which breaks destination-restricted discount codes like
    // FREESHIPCA (Shopify evaluates them against the stale cart-market
    // destination, not the URL context). The POST happens fire-and-forget,
    // since navigation immediately follows; Shopify's session cookies update
    // on the way out, ready for the next page load.
    var headerForm = document.querySelector('[data-header-country-form]');
    var cartForm   = document.querySelector('[data-cart-country-form]');
    var srcForm    = headerForm || cartForm;
    if (srcForm) {
      var fd = new FormData(srcForm);
      fd.set('country_code', country);
      if (!fd.has('_method')) fd.set('_method', 'PUT');
      // Fire-and-forget. keepalive lets the POST survive the imminent
      // navigation; no .then(), no waiting — we don't care about the response
      // because the next page load will pick up the new market via cookies.
      try {
        fetch('/localization', { method: 'POST', body: fd, credentials: 'same-origin', keepalive: true });
      } catch (e) { /* never block navigation */ }
    }

    var PREFIX = '/en-us';
    var path = window.location.pathname;
    // Strip any existing /en-us prefix first so toggling CA→US→CA→US doesn't
    // accumulate or get stuck.
    if (path.indexOf(PREFIX + '/') === 0 || path === PREFIX) {
      path = path.slice(PREFIX.length) || '/';
    }
    if (country === 'US') {
      path = PREFIX + path;
    }
    var dest = path + window.location.search + window.location.hash;
    // location.assign (vs .href =) makes the new entry navigable via Back.
    window.location.assign(dest);
    return Promise.resolve();
  }
  function syncCountryControls(country) {
    // Remember the choice so any subsequent cart re-render can re-apply it.
    lastCountryChoice = country;
    // Persist across page navigations (in case /localization didn't commit
    // server-side — e.g. local dev 401, or Shopify rejected the POST).
    try { sessionStorage.setItem('pennie-market-country', country); } catch (e) {}
    // Authoritative visual signal — read by the toggle CSS (html[data-market]).
    // Also set in an inline <head> script before first paint, so the toggle is
    // correct from the very first frame on new pages.
    document.documentElement.setAttribute('data-market', country);
    // Visual state: thumb position via .is-us on the toggle button(s).
    document.querySelectorAll('[data-country-toggle]').forEach(function (b) {
      b.classList.toggle('is-us', country === 'US');
      b.setAttribute('aria-label', 'Switch shipping country (currently ' + country + ')');
    });
    // Form state: hidden country_code inputs (the source of truth for getSelectedCountry).
    document.querySelectorAll('[data-country-input]').forEach(function (input) {
      input.value = country;
    });
  }
  // Toggle buttons flip CA ↔ US. Same handler for header and cart-drawer toggles.
  // Reads from html[data-market] (the authoritative state set by the inline <head>
  // script + syncCountryControls), which is correct from first paint even on a
  // cross-page navigation where the server-rendered toggle markup may have lagged.
  document.addEventListener('click', function (event) {
    var tog = event.target.closest('[data-country-toggle]');
    if (!tog) return;
    event.preventDefault();
    var current = document.documentElement.getAttribute('data-market') === 'US' ? 'US' : 'CA';
    switchCountry(current === 'US' ? 'CA' : 'US');
  });
  // Prevent the bare-form submit (e.g. Enter key inside the form) from page-
  // navigating; route through switchCountry. Submit-button values aren't in
  // FormData(form) unless the form is naturally submitted, so we prefer
  // event.submitter for the actual clicked side.
  document.addEventListener('submit', function (event) {
    var f = event.target.closest('[data-header-country-form], [data-cart-country-form]');
    if (!f) return;
    event.preventDefault();
    var country = '';
    if (event.submitter && event.submitter.name === 'country_code') {
      country = event.submitter.value;
    } else {
      var fd = new FormData(f);
      country = String(fd.get('country_code') || '');
    }
    switchCountry(country);
  });

  // ---- Market-aware sold-out detection ----------------------------------
  // Liquid's `item.product.available` doesn't reflect market-catalog exclusion
  // (a product can be globally `available` but excluded from the current
  // buyer's market). And Ajax `/products/<handle>.js` is unreliable here too —
  // when a product has alias handles (e.g. legacy handles after a rename), the
  // alias path may return 200 even when the canonical handle 404s in the new
  // market. The only reliable signal is the Storefront GraphQL API queried
  // with @inContext(country) — it returns null for products excluded from the
  // active market catalog. Cached per (country, product_id); cache for the
  // chosen country is dropped on switchCountry so it re-probes fresh.
  var marketAvailability = {};   // { CA: { '<gid>': bool }, US: { '<gid>': bool } }
  function markLineSoldOut(line, soldOut) {
    line.classList.toggle('cart-line--sold-out', soldOut);
    if (soldOut) line.setAttribute('data-cart-line-sold-out', '');
    else line.removeAttribute('data-cart-line-sold-out');
  }
  // Synchronously apply whatever we ALREADY know about market availability for
  // the current country. Called from refreshCart's innerHTML swap so the new
  // server-rendered lines never flash an "available" state before checkMarket-
  // Availability gets a chance to re-probe.
  function applyCachedMarketSoldOut(root) {
    var country = getSelectedCountry();
    var cache = marketAvailability[country];
    if (!cache) return;
    (root || document).querySelectorAll('[data-cart-line-product-id]').forEach(function (line) {
      var pid = line.getAttribute('data-cart-line-product-id');
      if (pid && pid in cache && cache[pid] === false) markLineSoldOut(line, true);
    });
  }
  function probeMarketAvailability(productIds, countryCode) {
    var token = window.PENNIE_STOREFRONT_TOKEN;
    if (!token || !productIds.length) return Promise.resolve({});
    // One query per product. nodes() is technically batchable but the per-product
    // contextual cost can be higher than one-call-per — and carts are small.
    var q = 'query($id:ID!,$cc:CountryCode!) @inContext(country:$cc){product(id:$id){id availableForSale}}';
    return Promise.all(productIds.map(function (pid) {
      var gid = 'gid://shopify/Product/' + pid;
      return fetch('/api/2025-07/graphql.json', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': token },
        body: JSON.stringify({ query: q, variables: { id: gid, cc: countryCode } })
      })
        .then(function (r) { return r.json(); })
        .then(function (d) {
          // null product → excluded from this market's catalog.
          // !availableForSale → sold out or unpublished in this market.
          var p = d && d.data && d.data.product;
          return { pid: pid, available: !!(p && p.availableForSale) };
        })
        .catch(function () { return { pid: pid, available: true }; });   // network blip → assume OK
    })).then(function (results) {
      var out = {};
      results.forEach(function (r) { out[r.pid] = r.available; });
      return out;
    });
  }
  function checkMarketAvailability() {
    var country = getSelectedCountry();
    if (!marketAvailability[country]) marketAvailability[country] = {};
    var cache = marketAvailability[country];
    var lines = Array.prototype.slice.call(document.querySelectorAll('[data-cart-line-product-id]'));
    if (!lines.length) return Promise.resolve();
    // Apply any already-known unavailability immediately so the UI is correct
    // before any network round-trip resolves (e.g. on second open after a switch).
    applyCachedMarketSoldOut();
    // Collect product ids that still need probing.
    var unknown = [];
    lines.forEach(function (line) {
      var pid = line.getAttribute('data-cart-line-product-id');
      if (pid && !(pid in cache)) unknown.push(pid);
    });
    if (!unknown.length) { syncCheckoutGate(); return Promise.resolve(); }
    return probeMarketAvailability(unknown, country).then(function (results) {
      Object.keys(results).forEach(function (pid) { cache[pid] = results[pid]; });
      lines.forEach(function (line) {
        var pid = line.getAttribute('data-cart-line-product-id');
        if (pid && cache[pid] === false) markLineSoldOut(line, true);
        // NOTE: never UN-mark — Liquid may have set sold-out for inventory
        // reasons; the market check only ADDS.
      });
      syncCheckoutGate();
    });
  }
  window.__pennieCheckMarketAvailability = checkMarketAvailability;

  // After every cart re-render, scan for market-unavailable lines (Liquid marks them
  // with data-cart-line-sold-out) and reflect it on the Checkout button + notice.
  function syncCheckoutGate() {
    var soldOut = document.querySelectorAll('[data-cart-line-sold-out]').length > 0;
    var btn = document.querySelector('[data-cart-checkout-btn]');
    var notice = document.querySelector('[data-cart-unavailable-notice]');
    if (btn) {
      btn.disabled = soldOut;
      btn.classList.toggle('is-disabled', soldOut);
      btn.setAttribute('aria-disabled', soldOut ? 'true' : 'false');
    }
    if (notice) notice.hidden = !soldOut;
  }

  // Clear the inline discount message the moment they edit the field.
  document.addEventListener('input', function (event) {
    if (event.target.closest('[data-cart-discount-input]')) hideDiscountMsg();
  });

  // Open from the header cart icon (anchor kept for no-JS fallback).
  document.addEventListener('click', function (event) {
    var toggle = event.target.closest('[data-cart-toggle]');
    if (toggle) {
      event.preventDefault();
      // Ignore post-navigation ghost clicks (see ghost-click guard up top).
      // Still preventDefault above so a stray ghost click can't navigate to
      // /cart either — we just don't open the drawer for it.
      if (!isGenuineActivation('[data-cart-toggle]')) return;
      // Open using the contents already server-rendered into the page. We do
      // NOT re-fetch + swap the drawer HTML here: on the first open after a
      // page load, the section-API markup differs trivially from the inline
      // render, so the swap would re-create every <img> and they'd blink once.
      // Contents are authoritative on load; cart mutations (add / qty / remove)
      // refresh them. Just keep the count badge fresh (cheap, no DOM swap).
      openCart();
      renderCartFooter();
      fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) { syncCartBadge(cart.item_count); })
        .catch(function () {});
      return;
    }
    // Close on backdrop / close-button / "continue shopping" link.
    if (event.target.closest('[data-cart-drawer-close]')) {
      // Let continue-shopping anchors still navigate.
      var link = event.target.closest('a[data-cart-drawer-close]');
      if (!link) event.preventDefault();
      closeCart();
      return;
    }
  });

  // Signal that the JS cart handler is live. The early inline guard in
  // theme.liquid <head> prevents the cart anchor from navigating to /cart
  // before this point; if a click came in during that window it set
  // __pennieCartPending, so honour it now by opening the drawer.
  window.__pennieCartReady = true;
  if (window.__pennieCartPending) {
    window.__pennieCartPending = false;
    openCart();
    refreshCart();
  }

  // Open the drawer when arriving with ?open-cart=1 (from /cart redirect — the
  // /cart route is stripped to a no-content redirect so customers returning to
  // their basket from checkout land on home with the drawer auto-opened). Strip
  // the param from the URL so a refresh doesn't keep re-opening.
  try {
    var params = new URLSearchParams(location.search);
    if (params.has('open-cart')) {
      openCart();
      params.delete('open-cart');
      var newSearch = params.toString();
      var cleanUrl = location.pathname + (newSearch ? '?' + newSearch : '') + location.hash;
      history.replaceState(null, '', cleanUrl);
    }
  } catch (e) {}

  // Restore the shopper's persisted country across page navigations. The server
  // renders the toggle from localization.country.iso_code, which may not reflect
  // their last choice if /localization hasn't committed (local dev 401, etc.).
  // If sessionStorage has a different country, apply it client-side so the toggle,
  // hidden input, and downstream logic all see the right value.
  try {
    var saved = sessionStorage.getItem('pennie-market-country');
    var serverCountry = (window.PENNIE_GEO_COUNTRY || 'CA').toUpperCase();
    if (saved && (saved === 'CA' || saved === 'US') && saved !== serverCountry) {
      syncCountryControls(saved);
    } else if (saved && (saved === 'CA' || saved === 'US')) {
      // Same as server but seed lastCountryChoice so cart re-renders carry the intent.
      lastCountryChoice = saved;
    }
  } catch (e) {}

  // Pre-populate the (still-hidden) footer on page load so the live shipping + total
  // are already filled — and the rate is cached — by the time the shopper opens the
  // cart. Without this, the server-rendered "Calculated at checkout" placeholder shows
  // for a frame on first open. No-ops on an empty cart (no footer rendered).
  renderCartFooter();
  // Same idea for the checkout-disabled gate: read the server-rendered sold-out flags
  // up-front, so the button reflects them before the drawer is even opened.
  syncCheckoutGate();
  // And probe market-catalog availability for each cart line in the current market —
  // catches products that Liquid's `product.available` flags as available globally
  // but the active market's catalog excludes.
  checkMarketAvailability();

  // Qty steppers + remove inside the drawer. Mirrors the +/- and set=0
  // semantics, but refreshes the drawer in place instead of reloading.
  document.addEventListener('click', function (event) {
    var btn = event.target.closest('[data-cart-drawer] [data-cart-qty-change]');
    if (!btn) return;
    event.preventDefault();
    var line = parseInt(btn.getAttribute('data-cart-qty-change'), 10);
    if (!line) return;

    var lineEl = document.querySelector('[data-cart-drawer] [data-cart-line="' + line + '"]');
    if (!lineEl) return;

    var quantity;
    if (btn.hasAttribute('data-cart-qty-set')) {
      quantity = parseInt(btn.getAttribute('data-cart-qty-set'), 10);
    } else {
      var delta = parseInt(btn.getAttribute('data-cart-qty-delta'), 10) || 0;
      var current = parseInt(lineEl.querySelector('[data-cart-qty-value]').textContent, 10) || 0;
      quantity = Math.max(0, current + delta);
    }

    lineEl.querySelectorAll('[data-cart-qty-change]').forEach(function (b) { b.disabled = true; });

    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ line: line, quantity: quantity })
    })
      .then(function (r) { return r.json(); })
      .then(function () { return refreshCart(); })
      .catch(function () {
        lineEl.querySelectorAll('[data-cart-qty-change]').forEach(function (b) { b.disabled = false; });
      });
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') closeCart();
  });

  // Intercept the PDP "Add to bag" form so it AJAX-adds and opens the
  // drawer instead of navigating to /cart. Falls back to a normal submit
  // only if fetch is unavailable (very old browsers).
  document.addEventListener('submit', function (event) {
    var form = event.target.closest('.pdp__form');
    if (!form || !window.fetch) return;
    event.preventDefault();

    var submitBtn = form.querySelector('[type="submit"]');
    if (submitBtn && submitBtn.disabled) return;
    var originalText = submitBtn ? submitBtn.textContent : null;
    if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = 'Adding…'; }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Accept': 'application/json' },
      body: new FormData(form)
    })
      .then(function (r) { return r.json(); })
      .then(function () { return refreshCart(); })
      .then(function () {
        openCart();
        if (submitBtn) submitBtn.textContent = 'Added';
      })
      .catch(function () {
        if (submitBtn) submitBtn.textContent = 'Try again';
      })
      .then(function () {
        setTimeout(function () {
          if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = originalText; }
        }, 1400);
      });
  });

  // ---- Product card hover/touch image swap -------------------------------
  // Hovering a card swaps to the product's SECOND image and holds there —
  // it does NOT keep cycling through the rest of the photos while you move
  // around. The card only snaps back to the featured (first) image once the
  // cursor leaves the card entirely (or touch ends).
  (function initCardHover() {
    const lastStep = new WeakMap();   // retained for the mouseout/touch resets
    const curIdx = new WeakMap();

    function setActive(media, idx) {
      const imgs = media.querySelectorAll('[data-card-image]');
      imgs.forEach((img, i) => img.classList.toggle('is-active', i === idx));
      curIdx.set(media, idx);
    }
    function step(media) {
      // Tablet + mobile (≤989px): no hover image-swap. There's no mouse, and
      // activating a stacked hover image (data-index 1, absolute inset:0) makes
      // it the visible top layer with edges on all four of .product-card__media's
      // rounded clip boundaries — which peeks a 1px image-coloured seam on the
      // card sides on mobile WebKit. Keeping only the cover (index 0) shown
      // avoids it. Gates both the mousemove (emulator) and touch paths.
      if (!window.matchMedia('(min-width: 990px)').matches) return;
      const count = parseInt(media.getAttribute('data-image-count') || '1', 10);
      if (count < 2) return;
      // Show ONLY the second image and stay on it; no further cycling.
      if (curIdx.get(media) === 1) return;
      setActive(media, 1);
    }

    document.addEventListener('mousemove', function (e) {
      const media = e.target.closest && e.target.closest('[data-card-media]');
      if (!media) return;
      step(media);
    });

    // mouseout fires for every element transition. We want to reset only when
    // the cursor leaves the CARD entirely — wherever inside the card it was
    // when it left (could be the image, the price pill, the cart button).
    // So: find the nearest enclosing card (not just media — that misses
    // cursor-leaves-via-cart-button), and only reset if the cursor's NEW
    // location (relatedTarget) is outside that card. mouseleave doesn't
    // bubble, so this delegated mouseout + contains() check stands in.
    document.addEventListener('mouseout', function (e) {
      const card = e.target.closest && e.target.closest('.browse-card, .product-card');
      if (!card) return;
      if (e.relatedTarget && card.contains(e.relatedTarget)) return;
      const media = card.querySelector('[data-card-media]');
      if (!media) return;
      lastStep.delete(media);
      setActive(media, 0);
    });

    // Touch: dragging cycles images on the same throttle. A normal tap-and-
    // release still navigates to the PDP because the anchor click fires.
    let touchTarget = null;
    document.addEventListener('touchstart', function (e) {
      const media = e.target.closest && e.target.closest('[data-card-media]');
      if (!media) return;
      const count = parseInt(media.getAttribute('data-image-count') || '1', 10);
      if (count < 2) return;
      touchTarget = media;
    }, { passive: true });
    document.addEventListener('touchmove', function () {
      if (!touchTarget) return;
      step(touchTarget);
    }, { passive: true });
    document.addEventListener('touchend', function () {
      if (!touchTarget) return;
      const m = touchTarget;
      touchTarget = null;
      // Brief pause so the last-shown image stays visible before snapping back
      setTimeout(() => { lastStep.delete(m); setActive(m, 0); }, 250);
    });
    document.addEventListener('touchcancel', function () {
      if (touchTarget) { lastStep.delete(touchTarget); setActive(touchTarget, 0); }
      touchTarget = null;
    });
  })();

  // ---- Coordinated skeleton reveal ----------------------------------------
  // ONE controller for every section's skeleton (hero, product-card carousels,
  // image-with-text). Every skeleton stays up until the WHOLE page has loaded
  // (window 'load' — fires only once every image is actually in) AND a minimum
  // on-screen time has elapsed, then all skeletons drop together so sections
  // never pop in piecemeal.
  //
  // Carousel images ship as loading="lazy", which window 'load' ignores. Since
  // the skeleton's whole job is to cover the page until everything's in, we
  // flip those to eager so they fetch now and are counted by 'load'. CSS
  // background images (image-with-text bg-fixed) also don't count toward
  // 'load', so we drop in a hidden probe <img> with the same src.
  (function initSkeletonReveal() {
    var MIN_MS = 1500;   // minimum shimmer time (avoids a flash on cached loads)
    var targets = [];    // elements that receive .is-loaded

    document.querySelectorAll('[data-hero-card]').forEach(function (card) {
      targets.push(card);
    });
    document.querySelectorAll('.product-card').forEach(function (card) {
      if (card.querySelector('.product-card__skeleton')) targets.push(card);
    });
    // NOTE: .browse-card (collection grid) is deliberately NOT revealed here.
    // Its products stream in via background-fetch after window 'load' and get
    // re-ordered by the Featured bag-sort, so the grid owns its own unison
    // reveal in collection.liquid (gated on the fetch chain finishing).
    document.querySelectorAll('.pdp__rec-card').forEach(function (card) {
      if (card.querySelector('.pdp__rec-card-skeleton')) targets.push(card);
    });
    // Mobile PDP video tiles (9:16 loaders). Their still images are lazy, so
    // the force-eager step below makes 'load' wait for them too.
    document.querySelectorAll('.pdp-ugc__tile').forEach(function (tile) {
      if (tile.querySelector('.pdp-ugc__skeleton')) targets.push(tile);
    });
    // Mobile PDP gallery thumbnail circles.
    document.querySelectorAll('.pdp__gallery-thumb').forEach(function (thumb) {
      if (thumb.querySelector('.pdp__gallery-thumb-skeleton')) targets.push(thumb);
    });
    // The rec container itself carries the "You may also like" heading loader.
    document.querySelectorAll('.pdp__rec').forEach(function (rec) {
      if (rec.querySelector('.pdp__rec-heading-skeleton')) targets.push(rec);
    });
    document.querySelectorAll('.pdp__details').forEach(function (el) {
      if (el.querySelector('.pdp__details-skeleton')) targets.push(el);
    });
    // Top-text loader runs on mobile + desktop (the in-place skeleton works at
    // any width since it's applied to the real child elements).
    document.querySelectorAll('.pdp__top-text').forEach(function (el) {
      targets.push(el);
    });
    // PDP gallery shimmer is desktop/tablet-only (on mobile the gallery is a
    // swipe strip with no shiner — its skeleton is display:none <750px). We
    // ALWAYS track it so it ALWAYS receives .is-loaded at reveal. Otherwise a
    // page that loads on mobile (gallery skipped) and is then widened to desktop
    // shows a shiner that nothing ever dismisses — it's orphaned, and the 5s cap
    // only reveals tracked elements. Marking it loaded on mobile is harmless
    // (the skeleton is hidden there). The force-eager step below stays gated to
    // ≥750 so we still don't eager-load the mobile swipe strip.
    var galleryDesktop = window.matchMedia('(min-width: 750px)').matches;
    document.querySelectorAll('.pdp__gallery').forEach(function (gallery) {
      if (gallery.querySelector('.pdp__gallery-skeleton')) targets.push(gallery);
    });
    document.querySelectorAll('.iwt-card').forEach(function (card) {
      targets.push(card);
      var src = card.getAttribute('data-iwt-img-src');
      if (src) {
        var probe = new Image();
        probe.alt = '';
        probe.setAttribute('aria-hidden', 'true');
        probe.style.cssText = 'position:absolute;width:1px;height:1px;opacity:0;pointer-events:none;';
        probe.src = src;
        card.appendChild(probe);   // appended so it counts toward window 'load'
      }
    });
    // Header country-toggle: tracked so its landing-page blush loader
    // (header.css) lifts in sync with every other skeleton on the page.
    // CSS scopes the loader visual to body.template-index, so adding
    // .is-loaded on every page is harmless — non-landing pages have no
    // overlay to hide.
    document.querySelectorAll('.header__country-form').forEach(function (form) {
      targets.push(form);
    });

    // Force the tracked sections' lazy images to load now so 'load' waits on them.
    targets.forEach(function (card) {
      // Don't eager-load the mobile PDP gallery swipe strip (perf): it's tracked
      // only so it gets .is-loaded, but its images shouldn't all fetch upfront
      // on mobile. On desktop the gallery is eagered like everything else.
      if (!galleryDesktop && card.classList.contains('pdp__gallery')) return;
      card.querySelectorAll('img[loading="lazy"]').forEach(function (img) {
        img.loading = 'eager';
      });
    });

    // ---- Hero background video ------------------------------------------
    // Pick ONE source by viewport + codec support, start buffering now, and
    // make the reveal wait until the browser reports it can play through.
    // The skeleton drop and video start then happen on the same frame.
    // Reduced-motion visitors keep the marquee (no video at all).
    var heroVideo = document.querySelector('[data-hero-card] [data-hero-video]');
    var videoDone = true;   // stays true when there's no video to wait for
    if (heroVideo && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      var isMobile = window.matchMedia('(max-width: 749px)').matches;
      var isTablet = !isMobile && window.matchMedia('(max-width: 989px)').matches;
      var pick = (function () {
        var a = function (n) { return heroVideo.getAttribute(n) || ''; };
        var mob = a('data-src-mobile');
        // Tablet gets its own 5:4 pair when set; otherwise the desktop pair.
        var webm = (isTablet && a('data-src-tablet-webm')) || a('data-src-webm');
        var hevc = (isTablet && a('data-src-tablet-hevc')) || a('data-src-hevc');
        if (isMobile && mob) return mob;
        if (webm && heroVideo.canPlayType('video/webm; codecs="av01.0.08M.08"')) return webm;
        if (hevc && heroVideo.canPlayType('video/mp4; codecs="hvc1.1.6.L153.B0"')) return hevc;
        return mob || hevc || webm;
      })();
      if (pick) {
        videoDone = false;
        var poster = isMobile
          ? (heroVideo.getAttribute('data-poster-mobile') || heroVideo.getAttribute('data-poster'))
          : isTablet
            ? (heroVideo.getAttribute('data-poster-tablet') || heroVideo.getAttribute('data-poster'))
            : heroVideo.getAttribute('data-poster');
        if (poster) heroVideo.poster = poster;
        heroVideo.muted = true;            // property too, not just the attribute (Safari autoplay)
        heroVideo.addEventListener('canplaythrough', onVideoReady, { once: true });
        heroVideo.addEventListener('error', function () { videoDone = true; maybeReveal(); }, { once: true });
        heroVideo.src = pick;
        heroVideo.load();
        if (heroVideo.readyState >= 4) onVideoReady();
      }
    }
    function startHeroVideo() {
      var card = heroVideo.closest('[data-hero-card]');
      if (card) card.classList.add('has-video-ready');
      try { heroVideo.currentTime = 0; } catch (e) {}
      var p = heroVideo.play();
      if (p && p.catch) p.catch(function () {});
    }
    function onVideoReady() {
      if (videoDone) return;
      videoDone = true;
      // Normal path: the reveal is still pending, so it will start the video
      // itself. Failsafe path (already revealed on the marquee): cut straight
      // to the video now.
      if (revealed) startHeroVideo(); else maybeReveal();
    }

    var MAX_MS = 5000;   // failsafe: a single forced-eager image that hangs must
                         // never lock the page — reveal no matter what by here.
    var minDone = false, loadDone = false, revealed = false;
    function doReveal() {
      if (revealed) return;
      revealed = true;
      if (heroVideo && videoDone && heroVideo.readyState >= 3) startHeroVideo();
      targets.forEach(function (t) { t.classList.add('is-loaded'); });
      // Cookie banner fades in a beat after the page appears (cookie-banner.css).
      setTimeout(function () { document.documentElement.classList.add('pc-ready'); }, 800);
    }
    function maybeReveal() { if (minDone && loadDone && videoDone) doReveal(); }
    setTimeout(function () { minDone = true; maybeReveal(); }, MIN_MS);
    setTimeout(doReveal, MAX_MS);   // last-resort cap (normally window 'load' wins first)
    if (document.readyState === 'complete') { loadDone = true; maybeReveal(); }
    else { window.addEventListener('load', function () { loadDone = true; maybeReveal(); }); }
  })();

  // ---- Cookie banner labels ----------------------------------------------
  // Shopify injects the banner with its own button text; relabel it to
  // "Accept all" and "Cookie preferences" as soon as it appears.
  (function () {
    function relabel() {
      var accept = document.getElementById('shopify-pc__banner__btn-accept');
      var prefs = document.getElementById('shopify-pc__banner__btn-manage-prefs');
      if (!accept && !prefs) return false;
      if (accept && !accept.querySelector('.pc-label')) accept.innerHTML = '<span class="pc-label">Accept all</span>';
      if (prefs) {
        var target = prefs.querySelector('span') || prefs;
        if (target.textContent.trim() !== 'Cookie preferences') target.textContent = 'Cookie preferences';
      }
      return !!(accept && prefs);
    }
    // Lock page scroll while the banner is on screen (only once it has
    // faded in, html.pc-ready); unlock when Shopify hides or removes it.
    function syncLock() {
      var b = document.getElementById('shopify-pc__banner');
      var open = !!b && document.documentElement.classList.contains('pc-ready') &&
        getComputedStyle(b).display !== 'none' && getComputedStyle(b).visibility !== 'hidden' &&
        b.getAttribute('aria-hidden') !== 'true';
      document.documentElement.classList.toggle('pc-lock', open);
    }
    relabel(); syncLock();
    new MutationObserver(function () { relabel(); syncLock(); })
      .observe(document.documentElement, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style', 'aria-hidden', 'hidden'] });
    // Tablet/desktop: slide the banner down out of view before Shopify
    // hides it. The first Accept click is held back while the banner
    // slides (cookie-banner.css .pc-slide-out), then passed on to Shopify.
    var sliding = false;
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('#shopify-pc__banner__btn-accept');
      if (!btn || sliding || !window.matchMedia('(min-width: 750px)').matches) return;
      var b = document.getElementById('shopify-pc__banner');
      if (!b) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      sliding = true;
      b.classList.add('pc-slide-out');
      setTimeout(function () { btn.click(); }, 400);
    }, true);
    document.addEventListener('click', function (e) {
      if (e.target.closest('#shopify-pc__banner__btn-accept, #shopify-pc__banner__btn-decline')) {
        setTimeout(syncLock, 0); setTimeout(syncLock, 500);
      }
    });
  })();

  // ---- Manage Cookies link ------------------------------------------------
  // Footer "Manage Cookies" link (href="#manage-cookies") re-opens Shopify's
  // built-in cookie consent banner so visitors can change their choices after
  // their first visit. Tries the Customer Privacy API first; falls back to
  // clearing the consent cookie and reloading (which retriggers the banner).
  document.addEventListener('click', function (e) {
    const link = e.target.closest('a[href="#manage-cookies"]');
    if (!link) return;
    e.preventDefault();
    const cp = window.Shopify && window.Shopify.customerPrivacy;
    if (cp && typeof cp.showPreferences === 'function') {
      cp.showPreferences();
      return;
    }
    if (cp && typeof cp.openPreferences === 'function') {
      cp.openPreferences();
      return;
    }
    // Fallback: clear the consent cookies and reload — Shopify's banner will
    // re-evaluate and show again on the next page load.
    ['_tracking_consent', 'tracking_consent', '_shopify_y', '_shopify_s'].forEach(function (name) {
      document.cookie = name + '=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
    });
    window.location.reload();
  });

  // ---- Policy pages: eyebrow + footnote -----------------------------------
  // Shopify renders /policies/* with hardcoded markup and silently ignores
  // any custom theme template (policy.liquid / policies.liquid). To give
  // policy pages the editorial feel, inject an eyebrow above the title and
  // a CTA footnote at the end so the page resolves instead of cutting off.
  const policyTitle = document.querySelector('.shopify-policy__title');
  if (policyTitle && !policyTitle.querySelector('.shopify-policy__eyebrow')) {
    const eyebrow = document.createElement('p');
    eyebrow.className = 'shopify-policy__eyebrow';
    eyebrow.textContent = 'Policies';
    policyTitle.insertBefore(eyebrow, policyTitle.firstChild);
  }
  // Shopify renders policy titles like "Privacy policy" / "Terms of service"
  // (sentence case). Normalize to title case for editorial consistency,
  // keeping short connectors (of/and/the/etc.) lowercase unless they're
  // the first word.
  const policyH1 = policyTitle && policyTitle.querySelector('h1');
  if (policyH1) {
    const stops = new Set(['of', 'and', 'the', 'or', 'for', 'to', 'in', 'on', 'a', 'an']);
    policyH1.textContent = policyH1.textContent.trim().toLowerCase().split(/\s+/)
      .map((w, i) => (i > 0 && stops.has(w)) ? w : w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  }
})();

/* ============================================================
   PDP details tabs — horizontal tabs (Details / Size / Care).
   Liquid pre-marks the first available tab and panel as .is-active
   (no-JS fallback). Click handler swaps .is-active across the
   tabs and panels in lockstep.
   ============================================================ */
(function () {
  document.querySelectorAll('.pdp__details').forEach(function (container) {
    const tabs = container.querySelectorAll('.pdp__details-tab');
    const panels = container.querySelectorAll('.pdp__details-panel');
    if (!tabs.length) return;
    tabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        const target = tab.getAttribute('data-tab');
        tabs.forEach(function (t) { t.classList.toggle('is-active', t === tab); });
        panels.forEach(function (p) { p.classList.toggle('is-active', p.getAttribute('data-panel') === target); });
      });
    });
  });
})();

/* ============================================================
   Mobile PDP gallery thumbnail circles.
   One button per gallery slide (rendered in main-product.liquid).
   Tap scrolls the gallery to that slide; scrolling marks the circle
   of the slide currently at the left edge as .is-active. Slides 2+
   have their own widths (440px tall, natural aspect), so positions
   come from each slide's offsetLeft rather than a fixed 100vw step.
   At the scroll end the last circle is active, since narrow final
   slides can't reach the left edge.
   ============================================================ */
(function () {
  const gallery = document.querySelector('.pdp__gallery');
  const thumbs = document.querySelectorAll('.pdp__gallery-thumb');
  if (!gallery || !thumbs.length) return;
  const items = gallery.querySelectorAll('.pdp__gallery-item, .pdp__gallery-placeholder');
  function slideLeft(i) { return items[i].offsetLeft - items[0].offsetLeft; }
  function updateActive() {
    if (window.innerWidth >= 750) return;
    const x = gallery.scrollLeft;
    let idx = 0;
    if (x >= gallery.scrollWidth - gallery.clientWidth - 2) {
      idx = Math.min(thumbs.length, items.length) - 1;
    } else {
      // The slide whose left edge is nearest the gallery's left edge.
      for (let i = 1; i < items.length && i < thumbs.length; i++) {
        if (Math.abs(slideLeft(i) - x) < Math.abs(slideLeft(idx) - x)) idx = i;
      }
    }
    thumbs.forEach(function (t, i) { t.classList.toggle('is-active', i === idx); });
  }
  thumbs.forEach(function (t) {
    t.addEventListener('click', function () {
      const i = parseInt(t.getAttribute('data-index'), 10);
      if (!items[i]) return;
      gallery.scrollTo({ left: slideLeft(i), behavior: 'smooth' });
    });
  });
  // Hero swipe-hint chevron: jump to the first gallery image.
  const next = document.querySelector('.pdp__gallery-next');
  if (next && items[1]) {
    next.addEventListener('click', function () {
      gallery.scrollTo({ left: slideLeft(1), behavior: 'smooth' });
    });
  }
  updateActive();
  gallery.addEventListener('scroll', updateActive, { passive: true });
})();

/* ============================================================
   Mobile PDP UGC video row + modal (markup in main-product.liquid).
   Tiles: each <video> keeps its mp4 in data-src until the row comes
   within 300px of the viewport, so nothing downloads with the first
   screen; then they play muted on loop. They pause while the row is
   scrolled well off screen (and while the modal is open) and resume
   when it's back. If play() is refused (iOS Low Power Mode) the still
   frame stays up. Tap a tile = modal on that video; swipe or the
   chevrons move between videos; X, tap outside the video or Escape
   closes. The page behind can't scroll: html.has-ugc-open switches
   scrolling off (overflow: hidden) and the modal's touch-action blocks
   vertical drags (main-product.css). The page never moves.
   ============================================================ */
(function () {
  const row = document.querySelector('[data-ugc-row]');
  const modal = document.querySelector('[data-ugc-modal]');
  if (!row || !modal) return;
  const tiles = Array.prototype.slice.call(row.querySelectorAll('.pdp-ugc__tile'));
  if (!tiles.length) return;
  const tileVideos = tiles.map(function (t) { return t.querySelector('.pdp-ugc__video'); });

  function tryPlay(v) {
    const p = v.play();
    if (p && p.catch) p.catch(function () {});
  }
  // Always silent, even if a video is uploaded with sound: muted + volume 0,
  // and anything that unmutes it (browser, extension) is undone immediately.
  function forceMute(v) {
    v.muted = true;
    v.defaultMuted = true;
    v.setAttribute('muted', '');
    try { v.volume = 0; } catch (e) {}   // read-only on iOS; muted covers it there
    v.addEventListener('volumechange', function () {
      if (!v.muted) v.muted = true;
    });
  }

  // ---- Tiles --------------------------------------------------
  let rowNear = false;
  let modalOpen = false;
  tileVideos.forEach(forceMute);
  tileVideos.forEach(function (v, i) {
    v.addEventListener('playing', function () { v.classList.add('is-playing'); });
    // If the 480p preview file fails, fall back to the 1080p one.
    v.addEventListener('error', function () {
      const full = tiles[i].dataset.ugcFull;
      if (full && v.getAttribute('src') !== full) {
        v.src = full;
        tryPlay(v);
      }
    });
  });
  function syncTiles() {
    tileVideos.forEach(function (v) {
      if (rowNear && !modalOpen) {
        if (!v.getAttribute('src')) v.src = v.dataset.src;
        tryPlay(v);
      } else if (!v.paused) {
        v.pause();
      }
    });
  }
  if ('IntersectionObserver' in window) {
    new IntersectionObserver(function (entries) {
      rowNear = entries[0].isIntersecting;
      syncTiles();
    }, { rootMargin: '300px 0px' }).observe(row);
  } else {
    rowNear = true;
    syncTiles();
  }

  // ---- Modal --------------------------------------------------
  // Each slide stacks two videos: `low` = the tile's 480p file (already
  // downloaded by the tile, so it plays the instant the modal opens, picking
  // up where the tile was) and `hd` = the 1080p file, started invisibly on
  // top. Once hd is playing it's lined up with low's time and faded in, and
  // low pauses. If hd never plays (slow network, Low Power Mode) low stays.
  // Every slide plays the whole time the modal is open (started inside the
  // opening tap), so a swipe lands on a video that's already moving instead
  // of a paused still that only starts once the swipe settles. HD loads
  // only for slides actually shown.
  document.body.appendChild(modal);   // out of .pdp__info so nothing can clip or stack over it
  const track = modal.querySelector('[data-ugc-track]');
  const prev = modal.querySelector('[data-ugc-prev]');
  const next = modal.querySelector('[data-ugc-next]');
  function modalVideo(cls, parent) {
    const v = document.createElement('video');
    v.className = cls;
    forceMute(v);
    v.loop = true;
    v.playsInline = true;
    v.setAttribute('loop', '');
    v.setAttribute('playsinline', '');
    v.preload = 'none';
    parent.appendChild(v);
    return v;
  }
  const slides = tiles.map(function (t, i) {
    const slide = document.createElement('div');
    slide.className = 'pdp-ugc-modal__slide';
    const media = document.createElement('div');
    media.className = 'pdp-ugc-modal__media';
    // Still frame as its own image UNDER the videos, not the video's poster:
    // iPhone drops a poster the moment playback starts, before the first
    // frame is drawn, which flashed the slide empty. The low video stays
    // invisible until it's actually playing (same as the tiles).
    // Shopify's generated still has lifted, milky blacks next to the real
    // video, so it's only the fallback; on open, frameStill paints the
    // tile's current frame over it (true colours, and the same frame the
    // video carries on from).
    if (t.dataset.ugcPoster) {
      const still = document.createElement('img');
      still.className = 'pdp-ugc-modal__still';
      still.src = t.dataset.ugcPoster;
      still.alt = '';
      media.appendChild(still);
    }
    const frame = document.createElement('canvas');
    frame.className = 'pdp-ugc-modal__still';
    frame.hidden = true;
    media.appendChild(frame);
    const s = {
      frame: frame,
      low: modalVideo('pdp-ugc-modal__video pdp-ugc-modal__video--low', media),
      hd: modalVideo('pdp-ugc-modal__video pdp-ugc-modal__video--hd', media),
      lowSrc: tileVideos[i].dataset.src,
      hdSrc: t.dataset.ugcFull,
      shown: false,     // hd faded in
    };
    s.low.addEventListener('playing', function () { s.low.classList.add('is-playing'); });
    // "+ More" pill, same as the tile's (main-product.liquid decides).
    if (t.hasAttribute('data-ugc-similar')) {
      const label = document.createElement('span');
      label.className = 'pdp-ugc-modal__label mini-caps-desktop';
      label.setAttribute('aria-hidden', 'true');
      label.textContent = '+ More';
      media.appendChild(label);
    }
    s.hd.addEventListener('playing', function () {
      if (s.shown || !modalOpen) return;
      if (s.low.readyState > 0 && !s.low.paused) {
        s.hd.addEventListener('seeked', function () { reveal(s); }, { once: true });
        s.hd.currentTime = s.low.currentTime;
      } else {
        reveal(s);
      }
    });
    // Low Power Mode fallback: a tap on the paused video starts it.
    media.addEventListener('click', function () { tryPlay(s.shown ? s.hd : s.low); });
    slide.appendChild(media);
    track.appendChild(slide);
    return s;
  });
  function reveal(s) {
    if (s.shown || !modalOpen) return;
    s.shown = true;
    s.hd.classList.add('is-ready');
    setTimeout(function () { if (s.shown) s.low.pause(); }, 200);   // after the fade
  }
  // Copy the tile's current frame into the slide's placeholder canvas.
  function frameStill(s, i) {
    const tv = tileVideos[i];
    if (s.shown || tv.readyState < 2 || !tv.videoWidth) return;
    try {
      s.frame.width = tv.videoWidth;
      s.frame.height = tv.videoHeight;
      s.frame.getContext('2d').drawImage(tv, 0, 0, tv.videoWidth, tv.videoHeight);
      s.frame.hidden = false;
    } catch (e) {}
  }
  // Start a slide's visible video (hd once it has taken over, else low).
  function startSlide(s, i) {
    if (s.shown) { tryPlay(s.hd); return; }
    if (!s.low.getAttribute('src')) {
      const t0 = tileVideos[i].currentTime || 0;
      s.low.addEventListener('loadedmetadata', function () {
        try { s.low.currentTime = t0; } catch (e) {}
      }, { once: true });
      s.low.src = s.lowSrc;
    }
    tryPlay(s.low);
  }
  function stopSlide(s) {
    if (!s.low.paused) s.low.pause();
    if (!s.hd.paused) s.hd.pause();
  }
  function startHd(s) {
    if (s.shown) return;
    if (!s.hd.getAttribute('src')) {
      s.hd.preload = 'auto';
      s.hd.src = s.hdSrc;
    }
    tryPlay(s.hd);   // iOS only buffers a video that's been asked to play
  }

  let current = 0;
  function setCurrent(i) {
    current = i;
    prev.classList.toggle('is-hidden', i <= 0);
    next.classList.toggle('is-hidden', i >= slides.length - 1);
    startHd(slides[i]);
  }
  function go(i) {
    if (i < 0 || i >= slides.length) return;
    track.scrollTo({ left: i * track.clientWidth, behavior: 'smooth' });
    setCurrent(i);   // inside the tap, so play() counts as user-started
  }
  let settleTimer;
  track.addEventListener('scroll', function () {
    clearTimeout(settleTimer);
    settleTimer = setTimeout(function () {
      const i = Math.round(track.scrollLeft / track.clientWidth);
      if (i !== current) setCurrent(i);
    }, 80);
  }, { passive: true });

  let hideTimer;
  function open(i) {
    clearTimeout(hideTimer);
    slides.forEach(frameStill);   // before syncTiles pauses the tiles
    modalOpen = true;
    syncTiles();
    document.documentElement.classList.add('has-ugc-open');
    modal.hidden = false;
    modal.setAttribute('aria-hidden', 'false');
    track.scrollLeft = i * track.clientWidth;   // after unhiding, so clientWidth is real
    slides.forEach(startSlide);                 // all of them, inside the tap
    setCurrent(i);
    requestAnimationFrame(function () { modal.classList.add('is-open'); });
  }
  function close() {
    if (!modalOpen) return;
    modalOpen = false;
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    slides.forEach(stopSlide);
    document.documentElement.classList.remove('has-ugc-open');
    hideTimer = setTimeout(function () { modal.hidden = true; }, 160);   // after the 150ms fade
    syncTiles();
  }

  tiles.forEach(function (t, i) {
    t.addEventListener('click', function () { open(i); });
    // Tiles are role="button" divs (a real <button> came out taller than its
    // contents on iPhone Safari), so give them a button's keyboard behaviour.
    t.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(i); }
    });
  });
  prev.addEventListener('click', function () { go(current - 1); });
  next.addEventListener('click', function () { go(current + 1); });
  modal.addEventListener('click', function (e) {
    if (e.target.closest('[data-ugc-close]') || e.target.classList.contains('pdp-ugc-modal__slide')) close();
  });
  document.addEventListener('keydown', function (e) {
    if (!modalOpen) return;
    if (e.key === 'Escape') close();
    else if (e.key === 'ArrowLeft') go(current - 1);
    else if (e.key === 'ArrowRight') go(current + 1);
  });
})();

/* ============================================================
   Mobile PDP header state triggers.

   Two body classes get toggled on the product page at mobile viewports
   to change the header's appearance based on scroll position:

     .is-gallery-past-first → user scrolled the horizontal gallery past
        the first image. Logo flips to ink. Header bg + other icons stay
        as their default (transparent + white) so there's no visible
        "white bar" during horizontal scroll.

     .is-past-gallery → user scrolled vertically far enough that the
        gallery's bottom edge is at or above the header's bottom edge.
        Full "stuck" treatment kicks in via CSS: paper bg + ink logo +
        ink icons. Matches the landing-page CTA-past behaviour.

   Both states are PDP-only and mobile-only. CSS lives in main-product.css.
   The trigger logic stops running (and removes the classes) at >=750vp.
   ============================================================ */
(function () {
  if (!document.body.classList.contains('template-product')) return;
  const gallery = document.querySelector('.pdp__gallery');
  const header = document.querySelector('[data-pennie-header]');
  if (!gallery || !header) return;

  const MOBILE_MAX = 749;
  function isMobile() { return window.innerWidth <= MOBILE_MAX; }

  function checkGalleryScroll() {
    if (!isMobile()) {
      document.body.classList.remove('is-gallery-past-first');
      return;
    }
    const firstItem = gallery.querySelector('.pdp__gallery-item, .pdp__gallery-placeholder');
    if (!firstItem) return;
    const itemWidth = firstItem.getBoundingClientRect().width;
    // Fire once the gallery is scrolled more than halfway off the first
    // image — matches the visual moment image 2 becomes more prominent.
    const past = gallery.scrollLeft > itemWidth * 0.5;
    document.body.classList.toggle('is-gallery-past-first', past);
  }

  function checkVerticalScroll() {
    if (!isMobile()) {
      document.body.classList.remove('is-past-gallery');
      return;
    }
    const eyebrow = document.querySelector('.pdp__eyebrow');
    const headerRect = header.getBoundingClientRect();
    let past;
    if (eyebrow) {
      // Fire when the eyebrow's top edge crosses the header's bottom
      // (i.e., the eyebrow is about to slip behind the header).
      past = eyebrow.getBoundingClientRect().top <= headerRect.bottom;
    } else {
      // Fallback: products without an eyebrow trigger off the gallery.
      past = gallery.getBoundingClientRect().bottom <= headerRect.bottom;
    }
    document.body.classList.toggle('is-past-gallery', past);
  }

  gallery.addEventListener('scroll', checkGalleryScroll, { passive: true });
  window.addEventListener('scroll', checkVerticalScroll, { passive: true });
  window.addEventListener('resize', function () {
    checkGalleryScroll();
    checkVerticalScroll();
  });

  // Initial run so the classes reflect the load-time state.
  checkGalleryScroll();
  checkVerticalScroll();
})();

/* ----- PDP "You may like" carousel — infinite loop via Embla Carousel -----
   Embla (self-hosted, loaded before this file on product pages) owns the drag,
   momentum and seamless infinite loop, so it works the same on every viewport
   and avoids the iOS native-scroll-snap flicker. It moves the .pdp__rec-track
   (the Embla "container") inside the overflow-hidden .pdp__rec-viewport via
   transforms — it does NOT clone DOM, so the 9 .pdp__rec-card elements (and the
   skeleton coordinator's count of them) are unchanged.
     - loop: true             → endless in both directions, capped at the 9 cards.
     - align: 'start'         → desktop/tablet 3-up, first card left-aligned.
     - align: 'center' ≤749px → mobile lands on a CENTERED card; with loop, the
                                last card peeks on the left + next on the right.
   Arrows drive scrollPrev/scrollNext (they wrap with loop). If Embla failed to
   load, we bail and the viewport just shows the first cards statically. */
(function () {
  const rec = document.querySelector('[data-pdp-rec]');
  if (!rec) return;
  const viewport = rec.querySelector('[data-pdp-rec-viewport]');
  const prev = rec.querySelector('[data-pdp-rec-prev]');
  const next = rec.querySelector('[data-pdp-rec-next]');
  if (!viewport || typeof window.EmblaCarousel !== 'function') return;

  // Match the landing carousel's pattern: branch by viewport at init time and
  // pass `align: 'center'` directly when on mobile (instead of `align: 'start'`
  // + breakpoints override). Embla's breakpoint switching can leave a brief
  // `start`-aligned render (cards packed from the left edge = "2-up flush"
  // loader flash) before flipping to `center`; passing the right align value
  // up front avoids that. Tablet + desktop still get `align: 'start'`.
  const isMobile = window.matchMedia('(max-width: 749px)').matches;
  const embla = window.EmblaCarousel(viewport, {
    loop: true,
    align: isMobile ? 'center' : 'start'
  });
  // reInit once everything has loaded so Embla re-measures slide positions
  // with images in (matches the landing carousel's `window.load → reInit`).
  if (isMobile) {
    window.addEventListener('load', () => embla.reInit());
  }

  if (prev) prev.addEventListener('click', () => embla.scrollPrev());
  if (next) next.addEventListener('click', () => embla.scrollNext());

  // Trackpad / horizontal wheel support — same debounced pattern as the landing
  // carousels above. We accumulate horizontal deltas during the gesture and fire
  // ONCE, 100ms after the wheel events stop, so a single swipe can never advance
  // more than one card. Vertical wheel falls through to the page scroll.
  let lastWheelAt = 0;
  viewport.addEventListener('wheel', (e) => {
    if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
    e.preventDefault();
    const now = Date.now();
    if (now - lastWheelAt < 350) return;
    if (e.deltaX > 4) { embla.scrollNext(); lastWheelAt = now; }
    else if (e.deltaX < -4) { embla.scrollPrev(); lastWheelAt = now; }
  }, { passive: false });

  // Images decode after init can shift slide sizes; re-measure once everything's
  // loaded so loop math + alignment stay exact.
  window.addEventListener('load', () => embla.reInit());
})();

/* PDP tablet "locked gallery" mode (main-product.css, 750-900 wide x 690-840
   tall): publish the gallery and info column heights so the CSS can give
   the section room for gallery-then-info scrolling. Heights are content
   heights (both columns are align-self: start), so setting them never
   feeds back into what is measured. */
(function () {
  const pdp = document.querySelector('.pdp');
  if (!pdp) return;
  const gal = pdp.querySelector('.pdp__gallery');
  const info = pdp.querySelector('.pdp__info');
  if (!gal || !info || typeof ResizeObserver !== 'function') return;
  const publish = () => {
    pdp.style.setProperty('--pdp-gal-h', gal.offsetHeight + 'px');
    pdp.style.setProperty('--pdp-info-h', info.offsetHeight + 'px');
  };
  const ro = new ResizeObserver(publish);
  // border-box: offsetHeight includes padding, and the info column's
  // padding changes at skeleton reveal without its content box changing.
  ro.observe(gal, { box: 'border-box' });
  ro.observe(info, { box: 'border-box' });
  window.addEventListener('resize', publish);
  window.addEventListener('load', publish);
  publish();
})();
